import { useState, useEffect } from 'react'
import { Modal, TextField, BlockStack, RadioButton, Select, InlineStack, Text } from '@shopify/polaris'
import { currencySymbol } from '../lib/currency'

export type ShippingData = {
    type: 'rate' | 'custom'
    rateName: string
    ratePrice: string
    customName: string
    customPrice: string
}

type Props = {
    open: boolean
    shipping: ShippingData
    hasShippableItems: boolean
    onSave: (shipping: ShippingData) => void
    onClose: () => void
}

export function ShippingModal({ open, shipping: initial, hasShippableItems, onSave, onClose }: Props) {
    const [shipping, setShipping] = useState<ShippingData>(initial)

    useEffect(() => {
        if (open) setShipping(initial)
    }, [open, initial])

    const handleDone = () => {
        if (shipping.type === 'custom') {
            const val = parseFloat(shipping.customPrice || '0')
            if (val < 0) return
        }
        onSave(shipping)
        onClose()
    }

    if (!hasShippableItems) {
        return (
            <Modal
                open={open}
                onClose={onClose}
                title="Unable to add shipping"
                primaryAction={{ content: 'Close', onAction: onClose }}
            >
                <Modal.Section>
                    <Text as="p">Shipping can't be added because there are no shippable items.</Text>
                </Modal.Section>
            </Modal>
        )
    }

    return (
        <Modal
            open={open}
            onClose={onClose}
            title="Shipping and delivery options"
            primaryAction={{ content: 'Done', onAction: handleDone }}
            secondaryActions={[{ content: 'Cancel', onAction: onClose }]}
        >
            <Modal.Section>
                <BlockStack gap="400">
                    <RadioButton
                        label="Shipping rates"
                        helpText="Select eligible shipping rates based on your Shipping settings"
                        checked={shipping.type === 'rate'}
                        onChange={() => setShipping(prev => ({ ...prev, type: 'rate' }))}
                    />
                    {shipping.type === 'rate' && (
                        <Select
                            label="Shipping rate"
                            labelHidden
                            options={[
                                { label: `Regular - ${currencySymbol('EUR')}2.00`, value: 'regular' },
                            ]}
                            value={shipping.rateName || 'regular'}
                            onChange={(v) => setShipping(prev => ({ ...prev, rateName: v, ratePrice: '2.00' }))}
                        />
                    )}
                    <RadioButton
                        label="Custom shipping"
                        checked={shipping.type === 'custom'}
                        onChange={() => setShipping(prev => ({ ...prev, type: 'custom' }))}
                    />
                    {shipping.type === 'custom' && (
                        <InlineStack gap="400">
                            <div style={{ flex: 1 }}>
                                <TextField
                                    label="Name"
                                    value={shipping.customName}
                                    onChange={(v) => setShipping(prev => ({ ...prev, customName: v }))}
                                    placeholder="Free shipping"
                                    autoComplete="off"
                                />
                            </div>
                            <div style={{ flex: 1 }}>
                                <TextField
                                    label="Price"
                                    value={shipping.customPrice}
                                    onChange={(v) => setShipping(prev => ({ ...prev, customPrice: v }))}
                                    autoComplete="off"
                                    type="number"
                                    prefix={currencySymbol('EUR')}
                                />
                            </div>
                        </InlineStack>
                    )}
                </BlockStack>
            </Modal.Section>
        </Modal>
    )
}
